import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, X } from "lucide-react";

import type { BlogLocale } from "@/features/blog/locales";
import { cn } from "@/common/utils";
import { LocaleLinks } from "@/features/blog/components/locale-links";
import { PillRow } from "@/features/blog/components/pill-row";
import { TagList } from "@/features/blog/components/tag-list";
import { BLOG_COPY, categoryLabel, visibleTags } from "@/features/blog/copy";
import { blogIndexLink } from "@/features/blog/links";
import { Pill } from "@/landing/system/links";

/**
 * Category pills, a Tags toggle that opens every tag, the active tag as a
 * removable chip, and the languages the blog is published in. Each row scrolls
 * sideways on phones.
 */
export function BlogFilters({
  locale,
  availableLocales,
  categories,
  tags,
  categorySlug,
  tagSlug,
}: {
  locale: BlogLocale;
  availableLocales: BlogLocale[];
  categories: { slug: string }[];
  tags: string[];
  categorySlug?: string;
  tagSlug?: string;
}) {
  const copy = BLOG_COPY[locale];
  const [tagsOpen, setTagsOpen] = useState(false);
  const panelId = useId();
  const shownTags = visibleTags(tags);

  return (
    <div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
        <PillRow>
          <Pill
            {...blogIndexLink(locale)}
            search={{ page: undefined, category: undefined, tag: undefined }}
            active={!categorySlug && !tagSlug}
          >
            {copy.all}
          </Pill>
          {categories.map((category) => (
            <Pill
              key={category.slug}
              {...blogIndexLink(locale)}
              search={{
                page: undefined,
                category: category.slug,
                tag: tagSlug,
              }}
              active={categorySlug === category.slug}
            >
              {categoryLabel(locale, category.slug)}
            </Pill>
          ))}

          {shownTags.length > 0 && (
            <button
              type="button"
              aria-expanded={tagsOpen}
              aria-controls={panelId}
              onClick={() => setTagsOpen((open) => !open)}
              className="g-glass g-glass-hover inline-flex h-9 flex-none cursor-pointer items-center gap-1.5 rounded-full border-0 pr-3 pl-4 text-[14px] text-(--g-ink-2) transition-colors hover:text-(--g-ink)"
            >
              {copy.tags}
              <ChevronDown
                aria-hidden
                className={cn(
                  "size-3.5 transition-transform",
                  tagsOpen && "rotate-180",
                )}
              />
            </button>
          )}

          {tagSlug && (
            <Link
              {...blogIndexLink(locale)}
              search={{
                page: undefined,
                category: categorySlug,
                tag: undefined,
              }}
              aria-label={`${copy.clearTag}: ${tagSlug}`}
              className="inline-flex h-9 flex-none items-center gap-1.5 rounded-full bg-(--g-ink) pr-3 pl-4 font-mono text-[12.5px] whitespace-nowrap text-(--g-lit) no-underline"
            >
              #{tagSlug}
              <X aria-hidden className="size-3.5 opacity-70" />
            </Link>
          )}
        </PillRow>

        <LocaleLinks
          current={locale}
          locales={availableLocales}
          renderLink={(alt, active, children) => (
            <Pill
              {...blogIndexLink(alt)}
              search={{ page: undefined, category: undefined, tag: undefined }}
              active={active}
            >
              {children}
            </Pill>
          )}
        />
      </div>

      {tagsOpen && (
        <div id={panelId} className="mt-5 max-w-[60rem]">
          <TagList tags={shownTags} locale={locale} activeTag={tagSlug} />
        </div>
      )}
    </div>
  );
}
